/**
 * Réponses des clients (e-mails reçus dans la boîte SAV, rangés par le serveur dans public.eds_inbound_emails).
 * Lecture et marquage « lu » : RLS can_use_feature (contact_messages / sav_requests). Aucune écriture du contenu depuis le navigateur.
 * Les textes sont toujours affichés en texte brut (jamais de HTML).
 */
import { SAV_PHOTO_LINK_SECONDS, SAV_PHOTOS_BUCKET } from './savRequestsService';
import { supabase } from './supabaseClient';

export interface InboundEmail {
  id: string;
  received_at: string;
  from_email: string;
  from_name: string | null;
  subject: string;
  body_text: string | null;
  attachment_count: number;
  attachment_names: string[];
  read_at: string | null;
  contact_message_id: string | null;
  web_request_id: string | null;
}

const COLUMNS =
  'id, received_at, from_email, from_name, subject, body_text, attachment_count, attachment_names, read_at, contact_message_id, web_request_id';

/** Photo jointe à une réponse du client (dossiers SAV seulement), avec un lien signé temporaire. */
export interface InboundPhotoLink {
  id: string;
  inbound_email_id: string;
  /** Lien signé de 5 minutes (null si la génération a échoué) */
  url: string | null;
}

export interface InboundUnreadCounts {
  contact: Record<string, number>;
  sav: Record<string, number>;
}

export interface InboundUnmatched {
  id: string;
  received_at: string;
  from_email: string;
  from_name: string | null;
  subject: string;
  match_status: 'unmatched' | 'ignored';
}

export const inboundEmailsService = {
  /** Réponses du client rattachées à un message Contact. */
  async listForContactMessage(messageId: string): Promise<InboundEmail[]> {
    const { data, error } = await supabase
      .from('eds_inbound_emails')
      .select(COLUMNS)
      .eq('contact_message_id', messageId)
      .eq('match_status', 'matched')
      .order('received_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as InboundEmail[];
  },

  /** Réponses du client rattachées à une demande SAV du site. */
  async listForSavRequest(webRequestId: string): Promise<InboundEmail[]> {
    const { data, error } = await supabase
      .from('eds_inbound_emails')
      .select(COLUMNS)
      .eq('web_request_id', webRequestId)
      .eq('match_status', 'matched')
      .order('received_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as InboundEmail[];
  },

  /** Photos jointes aux réponses du client d'une demande SAV (liens signés de 5 minutes, jamais d'URL publique). */
  async getSavPhotoLinks(webRequestId: string): Promise<InboundPhotoLink[]> {
    const { data, error } = await supabase
      .from('eds_inbound_attachments')
      .select('id, inbound_email_id, storage_path')
      .eq('web_request_id', webRequestId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    const rows = (data ?? []) as { id: string; inbound_email_id: string; storage_path: string }[];
    if (rows.length === 0) return [];
    const { data: signed, error: signError } = await supabase.storage
      .from(SAV_PHOTOS_BUCKET)
      .createSignedUrls(
        rows.map(r => r.storage_path),
        SAV_PHOTO_LINK_SECONDS
      );
    if (signError) throw signError;
    const byPath = new Map((signed ?? []).map(s => [s.path, s.error ? null : s.signedUrl]));
    return rows.map(r => ({
      id: r.id,
      inbound_email_id: r.inbound_email_id,
      url: byPath.get(r.storage_path) ?? null,
    }));
  },

  /** Marque lues (read_by = utilisateur connecté ; le trigger de garde refuse tout autre identifiant). */
  async markRead(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const { data: auth, error: authErr } = await supabase.auth.getUser();
    if (authErr || !auth.user) throw authErr ?? new Error('Session absente');
    const { error } = await supabase
      .from('eds_inbound_emails')
      .update({ read_at: new Date().toISOString(), read_by: auth.user.id })
      .in('id', ids)
      .is('read_at', null);
    if (error) throw error;
  },

  /** Nombre de réponses non lues par message / demande (la RLS ne renvoie que ce que l'utilisateur peut voir). */
  async getUnreadCounts(): Promise<InboundUnreadCounts> {
    const { data, error } = await supabase.rpc('get_inbound_unread_counts');
    if (error) throw error;
    const result: InboundUnreadCounts = { contact: {}, sav: {} };
    for (const row of (data ?? []) as { kind: string; ref_id: string; unread: number | string }[]) {
      const n = Number(row.unread);
      if (!row.ref_id || !Number.isFinite(n) || n <= 0) continue;
      if (row.kind === 'contact') result.contact[row.ref_id] = n;
      else if (row.kind === 'sav') result.sav[row.ref_id] = n;
    }
    return result;
  },

  /** Courriers non rattachés ou ignorés (super admin seul, RLS) : métadonnées seulement, jamais de texte. */
  async listUnmatched(): Promise<InboundUnmatched[]> {
    const { data, error } = await supabase
      .from('eds_inbound_emails')
      .select('id, received_at, from_email, from_name, subject, match_status')
      .in('match_status', ['unmatched', 'ignored'])
      .order('received_at', { ascending: false })
      .limit(200);
    if (error) throw error;
    return (data ?? []) as InboundUnmatched[];
  },

  /** Super admin : marque un courrier non rattaché comme ignoré (fonction serveur ignore_inbound_email). */
  async ignore(id: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('ignore_inbound_email', { p_id: id });
    if (error) throw error;
    return data === true;
  },
};
